/**
 * Camera rig — F5 cycles first person → third person (behind) →
 * third person (front), Minecraft style. In third person the camera
 * is pulled back along the view ray and clipped against blocks so it
 * never ends up inside terrain.
 */

import * as THREE from "three";
import {
  PLAYER_EYE_HEIGHT,
  raycastVoxels,
  getCameraDirection,
  type WorldBlockAccess,
} from "@craftgaming/shared";
import type { PlayerState } from "./player-controller";

export enum CameraMode {
  FIRST_PERSON = 0,
  THIRD_BACK = 1,
  THIRD_FRONT = 2,
}

const THIRD_PERSON_DISTANCE = 4;
const CAMERA_CLIP_MARGIN = 0.25;

export type CameraRig = {
  getMode: () => CameraMode;
  cycleMode: () => CameraMode;
  /** Positions + orients the camera for the current mode. */
  update: (camera: THREE.PerspectiveCamera, player: PlayerState) => void;
  isFirstPerson: () => boolean;
  dispose: () => void;
};

export function createCameraRig(worldAccess: WorldBlockAccess): CameraRig {
  let mode = CameraMode.FIRST_PERSON;

  function cycleMode(): CameraMode {
    mode = (mode + 1) % 3;
    return mode;
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.code === "F5") {
      e.preventDefault(); // don't reload the page
      cycleMode();
    }
  }
  document.addEventListener("keydown", handleKeyDown);

  function update(camera: THREE.PerspectiveCamera, player: PlayerState) {
    const eyeX = player.x;
    const eyeY = player.y + PLAYER_EYE_HEIGHT;
    const eyeZ = player.z;

    const look = getCameraDirection(player.yaw, player.pitch);

    if (mode === CameraMode.FIRST_PERSON) {
      camera.position.set(eyeX, eyeY, eyeZ);
      camera.quaternion.setFromEuler(new THREE.Euler(player.pitch, player.yaw, 0, "YXZ"));
      return;
    }

    // Third person: pull the camera along ±look, clipped against terrain
    const sign = mode === CameraMode.THIRD_BACK ? -1 : 1;
    const dirX = look.x * sign;
    const dirY = look.y * sign;
    const dirZ = look.z * sign;

    const hit = raycastVoxels(eyeX, eyeY, eyeZ, dirX, dirY, dirZ, THIRD_PERSON_DISTANCE, worldAccess);
    // Never place the camera past the hit point — with a wall right
    // behind the head, that would put it inside the block (x-ray).
    const distance = hit
      ? Math.max(0.05, hit.distance - CAMERA_CLIP_MARGIN)
      : THIRD_PERSON_DISTANCE;

    camera.position.set(
      eyeX + dirX * distance,
      eyeY + dirY * distance,
      eyeZ + dirZ * distance
    );

    if (mode === CameraMode.THIRD_BACK) {
      // Same orientation as first person — looking over the shoulder
      camera.quaternion.setFromEuler(new THREE.Euler(player.pitch, player.yaw, 0, "YXZ"));
    } else {
      // Front mode looks back at the player
      camera.lookAt(eyeX, eyeY, eyeZ);
    }
  }

  return {
    getMode: () => mode,
    cycleMode,
    update,
    isFirstPerson: () => mode === CameraMode.FIRST_PERSON,
    dispose() {
      document.removeEventListener("keydown", handleKeyDown);
    },
  };
}
